'use client';

import React, {useEffect, useCallback} from 'react';
import {observer} from 'mobx-react';
import classNames from 'classnames';

import {useClpCourse} from '@/features/course-landing-page/context/clp-course-provider.react-component';
import {useClpPurchaseContext} from '../../context/clp-purchase-context.react-component';
import {
    EnableCouponInputModeButton,
    RemoveCouponButton,
} from '../redeem-coupon/redeem-coupon.react-component';
import {GiftThisCourse} from '../gift-this-course';
import {RedeemCouponContainer} from '@/features/course-landing-page/components/redeem-coupon/redeem-coupon-container.react-component';
import {MarketplaceSocialShareButton} from '../share/marketplace-social-share-button.react-component';
import styles from './clp-icon-secondary-actions.module.scss';
import {MODES, DISCOUNT_STATUSES} from '../redeem-coupon/constants';
import {isDiscountRemovable} from '../redeem-coupon/utils';

export interface ClpIconSecondaryActionProps {
    className?: string;
}

/**
 * ClpIconSecondaryActions component renders secondary action buttons for course landing page
 * including social share, gift course, and coupon input mode buttons.
 *
 * This is a variation on the CLPSecondaryActions component, which renders an alternate layout which replaces certain buttons
 * with icon variants.
 */
export const ClpIconSecondaryActions = observer(function ClpIconSecondaryActions({
    className,
}: ClpIconSecondaryActionProps) {
    const {couponStore, handleCouponRemove, currentDiscountAttempts} = useClpPurchaseContext();
    const course = useClpCourse();
    const isPaid = !course.isFree;

    const hasRemovableDiscount = currentDiscountAttempts.some((discount) => {
        return isDiscountRemovable(discount);
    });

    const handleEnterInputMode = useCallback(() => {
        couponStore.enterInputMode();
    }, [couponStore]);

    // Because applied coupons are only displayed in input mode for this variant of the secondary actions,
    // if we have an applied coupon, automatically enter input mode.
    useEffect(() => {
        if (
            currentDiscountAttempts.some((discount) => {
                return discount.status === DISCOUNT_STATUSES.APPLIED;
            })
        ) {
            handleEnterInputMode();
        }
    }, [currentDiscountAttempts, handleEnterInputMode]);

    const handleRemoveCoupons = useCallback(() => {
        currentDiscountAttempts
            .filter((discount) => {
                return isDiscountRemovable(discount);
            })
            .map((discount) => {
                handleCouponRemove(discount.code);
            });
    }, [currentDiscountAttempts, handleCouponRemove]);

    const isCouponFormDisabled = [MODES.INPUT, MODES.PENDING].includes(couponStore.currentMode);

    return (
        <>
            <div className={classNames(styles['secondary-action-container'], className)}>
                {hasRemovableDiscount ? (
                    <RemoveCouponButton
                        onRemoveCoupon={handleRemoveCoupons}
                        removeCouponButtonProps={{
                            className: classNames(
                                'ud-link-underline',
                                'ud-link-neutral',
                                styles['coupon-input-button'],
                            ),
                        }}
                    />
                ) : (
                    <EnableCouponInputModeButton
                        onEnterInputMode={handleEnterInputMode}
                        enableCouponInputModeButtonProps={{
                            className: classNames(
                                'ud-link-underline',
                                'ud-link-neutral',
                                styles['coupon-input-button'],
                            ),
                            disabled:
                                isCouponFormDisabled ||
                                currentDiscountAttempts.some((discount) => {
                                    return discount.status === DISCOUNT_STATUSES.APPLIED;
                                }),
                        }}
                    />
                )}
                <span>
                    <GiftThisCourse onlyIcon />
                    <MarketplaceSocialShareButton
                        shareableObject={{
                            ...course,
                            is_private: false,
                        }}
                        size={'large'}
                        onlyIcon
                        sourceUrl={course.urlCourseLanding || ''}
                        context="clp"
                        className="ud-link-underline ud-link-neutral"
                        load={() =>
                            import(
                                '../share/marketplace-social-share-content.react-component'
                            ).then(({MarketplaceSocialShareContent}) => ({
                                default: MarketplaceSocialShareContent,
                            }))
                        }
                    />
                </span>
            </div>
            <RedeemCouponContainer
                is_paid={isPaid}
                showCouponInputModeButton={false}
                showDiscountsAsTextInput={true}
                size={'small'}
                submitButtonStyle={'secondary'}
                inputModeClassName={styles['redeem-coupon-input-mode']}
            />
        </>
    );
});
